import React from "react";
import { ROOMS } from "../../data/site";
import { Check } from "lucide-react";

export default function ThemedRooms({ onBook }) {
  return (
    <section id="rooms" data-testid="rooms-section" className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="inline-block bg-brand-pink text-white font-fredoka font-bold rounded-full px-4 py-1.5 border-4 border-slate-900 neo-shadow-sm rotate-[-2deg]">
            FOUR MAGICAL ROOMS
          </span>
          <h2 className="font-fredoka font-bold text-4xl sm:text-5xl lg:text-6xl mt-6 text-slate-900">
            Pick a favorite. <span className="text-brand-red">If you can.</span>
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-700">
            Every bedroom is hand-painted and themed top to bottom. No two are alike, and all four are unforgettable.
          </p>
          <p
            data-testid="rooms-included-note"
            className="mt-5 inline-block bg-white border-4 border-slate-900 rounded-full px-5 py-2 neo-shadow-sm font-fredoka font-bold text-sm sm:text-base text-slate-900"
          >
            All four bedrooms are included in your stay.
          </p>
        </div>

        <div className="space-y-20 lg:space-y-28">
          {ROOMS.map((room, i) => {
            const flip = i % 2 === 1;
            return (
              <div
                key={room.key}
                data-testid={`room-card-${room.key}`}
                className={`grid lg:grid-cols-2 gap-8 lg:gap-14 items-center ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}
              >
                {/* Image */}
                <div className="relative">
                  <div
                    className="absolute -inset-3 rounded-[2.2rem] border-4 border-slate-900"
                    style={{ background: room.bg }}
                  />
                  <img
                    src={room.image}
                    alt={room.name}
                    className="relative w-full h-[340px] sm:h-[440px] object-cover rounded-3xl border-4 border-slate-900 neo-shadow-lg"
                  />
                  <span
                    className="absolute -top-4 -right-3 z-10 inline-flex items-center justify-center w-16 h-16 text-3xl bg-white border-4 border-slate-900 rounded-full neo-shadow-sm rotate-[-8deg] animate-wiggle"
                  >
                    {room.emoji}
                  </span>
                </div>
                {/* Content */}
                <div>
                  <span
                    className="inline-block font-fredoka font-bold text-sm tracking-wide uppercase border-4 border-slate-900 rounded-full px-4 py-1 neo-shadow-sm"
                    style={{ background: room.accent, color: "#0f172a" }}
                  >
                    {room.tagline}
                  </span>
                  <h3 className="font-fredoka font-bold text-3xl sm:text-4xl lg:text-5xl mt-4 text-slate-900">
                    {room.name}
                  </h3>
                  <p className="mt-3 text-lg text-slate-700 leading-relaxed">{room.description}</p>

                  <ul className="mt-6 grid grid-cols-2 gap-3">
                    {room.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-center gap-2 bg-white border-4 border-slate-900 rounded-2xl px-3 py-2 neo-shadow-sm"
                      >
                        <Check size={18} className="text-brand-green flex-shrink-0" />
                        <span className="font-quicksand font-semibold text-sm text-slate-900">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Single whole-home CTA */}
        <div className="mt-16 sm:mt-20 flex flex-col items-center text-center">
          <p className="max-w-xl text-lg text-slate-700 font-quicksand">
            Book the <span className="font-bold text-slate-900">entire home</span> — all four themed bedrooms, sleeps up to 12.
          </p>
          <button
            data-testid="rooms-wholehome-cta"
            onClick={onBook}
            className="mt-5 font-fredoka font-bold text-xl bg-brand-red text-white border-4 border-slate-900 rounded-full px-8 py-4 neo-shadow neo-press"
          >
            Check Whole-Home Availability →
          </button>
        </div>
      </div>
    </section>
  );
}
